import { useEffect, useRef, useState } from 'react';
import SignaturePad from 'signature_pad';
import type { SignatureAnswer } from '../logic/form-logic';
import type { FormCopy } from './copy';
import { cx, FOCUS_RING, FormButton } from './ui';

/** The largest drawing kept, in pixels: the server refuses anything bigger. */
const MAX_WIDTH = 1200;
const MAX_HEIGHT = 600;
const HEIGHT_CSS = 180;

/**
 * A signature, drawn in a box or typed (SFM-D1).
 *
 * Typing is not a fallback hidden behind the drawing: it is how somebody using
 * a keyboard or a screen reader signs, so it is one button away and says what
 * it means. A drawing is kept as a PNG without its `data:` prefix; the server
 * re-encodes it before it is stored.
 *
 * Where the canvas cannot draw at all (an old browser, or a test DOM), only the
 * typed name is offered.
 */
export function SignatureField({
  id,
  value,
  onChange,
  copy,
  describedBy,
  invalid,
}: {
  id: string;
  value: SignatureAnswer | undefined;
  onChange: (value: SignatureAnswer | undefined) => void;
  copy: FormCopy;
  describedBy?: string;
  invalid: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePad | null>(null);
  const [canDraw, setCanDraw] = useState(true);
  const [mode, setMode] = useState<'drawn' | 'typed'>(
    value?.kind === 'typed' ? 'typed' : 'drawn',
  );
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (mode !== 'drawn') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    let pad: SignaturePad;
    try {
      const context = canvas.getContext('2d');
      if (!context) throw new Error('no 2d context');
      // Sharp on a high-density screen, but never bigger than the server keeps.
      const cssWidth = canvas.offsetWidth || 340;
      const ratio = Math.min(
        Math.max(window.devicePixelRatio || 1, 1),
        MAX_WIDTH / cssWidth,
        MAX_HEIGHT / HEIGHT_CSS,
      );
      canvas.width = Math.round(cssWidth * ratio);
      canvas.height = Math.round(HEIGHT_CSS * ratio);
      context.scale(ratio, ratio);
      pad = new SignaturePad(canvas, {
        // Transparent, so the PDF shows the ink on its own paper.
        backgroundColor: 'rgba(0,0,0,0)',
        penColor: '#1b1b1f',
        onEnd: () => {
          const png = pad
            .toDataURL('image/png')
            .replace(/^data:image\/png;base64,/, '');
          onChangeRef.current({ kind: 'drawn', png });
        },
      });
      if (value?.kind === 'drawn') {
        pad.fromDataURL(`data:image/png;base64,${value.png}`, {
          ratio,
          width: cssWidth,
          height: HEIGHT_CSS,
        });
      }
    } catch {
      setCanDraw(false);
      setMode('typed');
      return;
    }
    padRef.current = pad;
    return () => {
      pad.off();
      padRef.current = null;
    };
    // Built once per mode, on purpose: rebuilding on every answer would wipe
    // the stroke the patient just drew. `value` is read only to restore one.
  }, [mode]);

  const clear = () => {
    padRef.current?.clear();
    onChange(undefined);
  };

  const switchTo = (next: 'drawn' | 'typed') => {
    setMode(next);
    onChange(undefined);
  };

  if (mode === 'typed') {
    return (
      <div className="space-y-3">
        <div>
          <label htmlFor={`${id}-name`} className="block text-base font-medium text-foreground">
            {copy.typedName}
          </label>
          <p id={`${id}-name-help`} className="mt-1 text-base text-muted-foreground">
            {copy.typedNameHelp}
          </p>
          <input
            id={`${id}-name`}
            type="text"
            autoComplete="name"
            maxLength={120}
            value={value?.kind === 'typed' ? value.name : ''}
            onChange={(event) =>
              onChange(
                event.target.value === ''
                  ? undefined
                  : { kind: 'typed', name: event.target.value },
              )
            }
            aria-invalid={invalid || undefined}
            aria-describedby={cx(`${id}-name-help`, describedBy) || undefined}
            className={cx(
              'mt-2 w-full rounded-lg border border-input bg-card px-3.5 py-2.5 text-xl text-foreground shadow-sm',
              'aria-[invalid=true]:border-danger',
              FOCUS_RING,
            )}
          />
        </div>
        {canDraw && (
          <FormButton variant="link" onClick={() => switchTo('drawn')}>
            {copy.drawInstead}
          </FormButton>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p id={`${id}-help`} className="text-base text-muted-foreground">
        {copy.signatureHelp}
      </p>
      <canvas
        ref={canvasRef}
        id={`${id}-canvas`}
        role="img"
        aria-label={copy.signatureCanvas}
        aria-describedby={cx(`${id}-help`, describedBy) || undefined}
        className={cx(
          'block w-full touch-none rounded-lg border-2 border-dashed border-input bg-card',
          invalid ? 'border-danger' : 'border-border',
        )}
        style={{ height: HEIGHT_CSS }}
      />
      {value?.kind === 'drawn' && (
        <p role="status" className="text-base text-muted-foreground">
          {copy.signatureDrawn}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <FormButton variant="outline" onClick={clear}>
          {copy.clearSignature}
        </FormButton>
        <FormButton variant="link" onClick={() => switchTo('typed')}>
          {copy.typeInstead}
        </FormButton>
      </div>
    </div>
  );
}
